import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type InfiniteData,
} from "@tanstack/react-query";
import { motion } from "framer-motion";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppSidebar } from "@/components/dashboard/AppSidebar";
import { ProjectCard, type Project } from "@/components/dashboard/ProjectCard";
import {
  Search,
  ChevronDown,
  Grid3x3,
  LayoutGrid,
  List,
  Star,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { createProjectsApi } from "@/lib/api/projects";
import type { PaginatedProjectList, ProjectListItem } from "@/lib/api/projects";

type ViewMode = "small-grid" | "large-grid" | "list";

const PAGE_SIZE = 12;

const SORT_MAP: Record<string, string> = {
  "last-viewed": "last_viewed_at",
  "last-edited": "updated_at",
  created: "created_at",
  name: "name",
};

const ORDER_MAP: Record<string, string> = {
  newest: "desc",
  oldest: "asc",
};

function formatEditedTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return `Edited ${date.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })}`;
  } else if (days > 0) {
    return `Edited ${days} day${days === 1 ? "" : "s"} ago`;
  } else if (hours > 0) {
    return `Edited ${hours} hour${hours === 1 ? "" : "s"} ago`;
  } else if (minutes > 0) {
    return `Edited ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  } else {
    return "Edited just now";
  }
}

function formatCreatedDate(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function toProject(p: ProjectListItem, user: any): Project {
  return {
    id: p.id,
    title: p.name,
    editedTime: formatEditedTime(p.updatedAt),
    createdAt: formatCreatedDate(p.createdAt),
    userAvatar: user?.imageUrl,
    userName: user?.fullName,
  };
}

export function StarredPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("large-grid");
  const [sortBy, setSortBy] = useState("last-viewed");
  const [sortOrder, setSortOrder] = useState("newest");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const projectsApi = createProjectsApi({ getToken });

  const queryKey = [
    "projects",
    "starred",
    { search: debouncedSearch, sortBy, sortOrder },
  ];

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam = 0 }) =>
        projectsApi.list({
          search: debouncedSearch || undefined,
          sortBy: SORT_MAP[sortBy] ?? "last_viewed_at",
          sortOrder: ORDER_MAP[sortOrder] ?? "desc",
          limit: PAGE_SIZE,
          offset: pageParam,
          starred: true,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage) =>
        lastPage.hasMore ? lastPage.offset + lastPage.limit : undefined,
      placeholderData: keepPreviousData,
    });

  const projects = data?.pages.flatMap((page) => page.items) ?? [];
  const totalStarred = data?.pages[0]?.total ?? 0;
  const isEmpty = !isLoading && totalStarred === 0 && !debouncedSearch;

  // Scroll first new card into view after load more completes
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  const handleLoadMore = () => {
    prevCountRef.current = projects.length;
    fetchNextPage();
  };

  useEffect(() => {
    if (
      !isFetchingNextPage &&
      projects.length > prevCountRef.current &&
      prevCountRef.current > 0
    ) {
      const firstNew = gridRef.current?.children[prevCountRef.current] as
        | HTMLElement
        | undefined;
      const container = scrollContainerRef.current;
      if (firstNew && container) {
        const cardTop = firstNew.getBoundingClientRect().top;
        const containerTop = container.getBoundingClientRect().top;
        container.scrollBy({ top: cardTop - containerTop, behavior: "smooth" });
      }
    }
  }, [isFetchingNextPage, projects.length]);

  const starMutation = useMutation({
    mutationFn: (projectId: string) => projectsApi.star(projectId),
    onMutate: async (projectId) => {
      await queryClient.cancelQueries({ queryKey });
      const prev =
        queryClient.getQueryData<InfiniteData<PaginatedProjectList>>(queryKey);
      queryClient.setQueryData<InfiniteData<PaginatedProjectList>>(
        queryKey,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((p) =>
                p.id === projectId ? { ...p, isStarred: !p.isStarred } : p,
              ),
            })),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      queryClient.setQueryData(queryKey, ctx?.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  return (
    <TooltipProvider>
      <SidebarProvider className="h-svh min-h-0! overflow-hidden">
        <AppSidebar />
        <SidebarInset className="max-h-svh overflow-hidden bg-surface-raised">
          <div
            ref={scrollContainerRef}
            className="relative flex h-svh flex-col overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 z-10 backdrop-blur">
              <div className="flex h-16 items-center justify-between px-4 md:px-6">
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-semibold">Starred</h1>
                </div>
              </div>

              {!isEmpty && (
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 px-4 md:px-6 pb-4">
                  {/* Search */}
                  <div className="relative w-auto md:w-auto lg:w-full lg:max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search starred projects..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9 bg-muted/50 border-border/50"
                    />
                  </div>

                  {/* Filters and view toggles */}
                  <div className="flex flex-wrap items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-[140px] h-9 justify-between bg-background"
                        >
                          <span>
                            {sortBy === "last-viewed" && "Last viewed"}
                            {sortBy === "last-edited" && "Last edited"}
                            {sortBy === "created" && "Created"}
                            {sortBy === "name" && "Name"}
                          </span>
                          <ChevronDown className="h-4 w-4 opacity-50" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-[200px] bg-surface-raised">
                        <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                        <DropdownMenuRadioGroup
                          value={sortBy}
                          onValueChange={setSortBy}
                        >
                          <DropdownMenuRadioItem value="last-viewed">
                            Last viewed
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="last-edited">
                            Last edited
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="created">
                            Created
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="name">
                            Name
                          </DropdownMenuRadioItem>
                          {/* <DropdownMenuRadioItem value="relevance">
                            <span className="flex items-center gap-2">
                              Relevance
                              <Info className="h-3 w-3 text-muted-foreground" />
                            </span>
                          </DropdownMenuRadioItem> */}
                        </DropdownMenuRadioGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>Order</DropdownMenuLabel>
                        <DropdownMenuRadioGroup
                          value={sortOrder}
                          onValueChange={setSortOrder}
                        >
                          <DropdownMenuRadioItem value="newest">
                            Newest first
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="oldest">
                            Oldest first
                          </DropdownMenuRadioItem>
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* View toggles */}
                    <div className="flex items-center gap-1 ml-auto bg-muted/30 border border-border/50 rounded-[1.25rem] p-[3px] relative">
                      {(["small-grid", "large-grid", "list"] as const).map(
                        (mode) => (
                          <button
                            key={mode}
                            onClick={() => setViewMode(mode)}
                            className={`relative z-10 flex items-center justify-center rounded-[1rem] p-2 transition-colors duration-200 ${
                              viewMode === mode
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                            aria-label={`${mode} view`}
                          >
                            {viewMode === mode && (
                              <motion.div
                                layoutId="starred-view-toggle"
                                className="absolute inset-0 bg-background shadow-sm rounded-[1rem] border border-border/50"
                                transition={{
                                  type: "spring",
                                  bounce: 0.2,
                                  duration: 0.6,
                                }}
                                style={{ zIndex: -1 }}
                              />
                            )}
                            {mode === "small-grid" && (
                              <Grid3x3 className="h-[18px] w-[18px]" />
                            )}
                            {mode === "large-grid" && (
                              <LayoutGrid className="h-[18px] w-[18px]" />
                            )}
                            {mode === "list" && (
                              <List className="h-[18px] w-[18px]" />
                            )}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : isEmpty ? (
              /* Empty state */
              (<div className="flex flex-1 flex-col items-center justify-center px-4 pb-24">
                <div className="flex flex-col items-center text-center max-w-md">
                  <Star className="size-12 text-muted-foreground/50 mb-6" />
                  <h2 className="text-2xl font-semibold text-foreground mb-3">
                    Star projects to access them quickly from any workspace
                  </h2>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate("/dashboard/projects")}
                  >
                    Browse projects
                  </Button>
                </div>
              </div>)
            ) : (
              /* Projects grid */
              (<div className="flex-1 px-4 md:px-6 py-6">
                {viewMode === "list" && projects.length > 0 && (
                  <div className="hidden md:flex items-center w-full px-4 py-2 border-b border-border/50 text-xs font-medium text-muted-foreground mb-4">
                    <div className="w-20 mr-4 shrink-0"></div>
                    <div className="flex-2 pr-4">Name</div>
                    <div className="flex-1 pr-4">Created at</div>
                    <div className="hidden lg:block flex-[1.5] pr-4">
                      Created by
                    </div>
                    <div className="w-10 shrink-0"></div>
                  </div>
                )}
                <div
                  ref={gridRef}
                  className={`grid ${
                    viewMode === "small-grid"
                      ? "gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                      : viewMode === "large-grid"
                        ? "gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                        : "gap-2 grid-cols-1"
                  }`}
                >
                  {projects.map((project, index) => (
                    <ProjectCard
                      key={project.id}
                      project={toProject(project, user)}
                      index={index % PAGE_SIZE}
                      isStarred={project.isStarred}
                      viewMode={viewMode}
                      onToggleStar={(id) => starMutation.mutate(id)}
                      onClick={() => navigate(`/projects/${project.id}`)}
                    />
                  ))}
                </div>
                {projects.length === 0 && debouncedSearch && (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <p className="text-muted-foreground">
                      No starred projects match &quot;{debouncedSearch}&quot;
                    </p>
                  </div>
                )}
                {hasNextPage && (
                  <div className="flex justify-center pt-6">
                    <Button
                      variant="outline"
                      onClick={handleLoadMore}
                      disabled={isFetchingNextPage}
                    >
                      {isFetchingNextPage && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      Load more
                    </Button>
                  </div>
                )}
              </div>)
            )}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
